import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
    changeUserNotice,
    clearUserNotices,
    clearUserNoticeStatus,
    getUserNoticeBatches,
    getUserNotices,
    stopUserNoticeBatch
} from '../../store/actions/userNoticesActions';
import ConfirmationModal from '../_common/ConfirmationModal';
import Spinner from '../_common/Spinner';
import NoticeUserPicker from './NoticeUserPicker';
import UserNoticeDetailsModal from './UserNoticeDetailsModal';
import {actionLabel, phaseLabel} from './noticeHelpers';
import './UserNotices.scss';

const selector = state => {
    const {language, userNotices} = state;
    return {
        t: language.translator,
        batches: userNotices.batches,
        batchesPending: userNotices.batchesPending,
        notices: userNotices.notices,
        listPending: userNotices.listPending,
        changePending: userNotices.changePending,
        error: userNotices.error
    };
};

const formatTime = value => (value ? String(value).replace('T', ' ').substring(0, 16) : '-');

const UserNotices = props => {
    const {t, batches, batchesPending, notices, listPending, changePending, error} = useSelector(selector);
    const dispatch = useDispatch();
    const created = props.location && props.location.state && props.location.state.created;

    const [tab, setTab] = useState('campaigns');
    const [user, setUser] = useState(null);
    const [stopping, setStopping] = useState(null);
    const [details, setDetails] = useState(null);

    useEffect(() => {
        dispatch(clearUserNoticeStatus());
        dispatch(clearUserNotices());
        dispatch(getUserNoticeBatches());
    }, [dispatch]);

    const pickUser = picked => {
        setUser(picked);
        dispatch(getUserNotices(picked.id, false));
    };

    const stopBatch = () => {
        const batchId = stopping.batchId;
        setStopping(null);
        dispatch(stopUserNoticeBatch(batchId));
    };

    return (
        <div className='userNotices'>
            <div className='userNoticesHeader'>
                {t.gettext('In-app messages')}
                <button
                    className='userNoticesNew'
                    onClick={() => props.history.push('/user_messages/new')}
                >
                    <span className='material-icons'>add</span>
                    {t.gettext('New message')}
                </button>
            </div>
            {created > 0 &&
                <div className='userNoticesFlash'>
                    {`${t.gettext('Messages created')}: ${created}`}
                </div>
            }
            {error && <div className='userNoticesError'>{error}</div>}
            <div className='userNoticesTabs'>
                <button
                    className={tab === 'campaigns' ? 'userNoticesTab userNoticesTab--active' : 'userNoticesTab'}
                    onClick={() => setTab('campaigns')}
                >
                    {t.gettext('Campaigns')}
                </button>
                <button
                    className={tab === 'user' ? 'userNoticesTab userNoticesTab--active' : 'userNoticesTab'}
                    onClick={() => setTab('user')}
                >
                    {t.gettext('Messages of a user')}
                </button>
            </div>

            {tab === 'campaigns' &&
                <div className='userNoticesCard'>
                    {batchesPending && <Spinner/>}
                    {!batchesPending && !batches.length &&
                        <div className='userNoticesMuted'>{t.gettext('No campaigns yet')}</div>
                    }
                    {batches.length > 0 &&
                        <div className='userNoticesTableScroll'>
                            <table className='userNoticesTable'>
                                <thead>
                                    <tr>
                                        <th>{t.gettext('Campaign')}</th>
                                        <th>{t.gettext('Created')}</th>
                                        <th>{t.gettext('Users')}</th>
                                        <th>{t.gettext('Active')}</th>
                                        <th>{t.gettext('Shown')}</th>
                                        <th>{t.gettext('Blocked')}</th>
                                        <th>{t.gettext('Confirmed')}</th>
                                        <th/>
                                    </tr>
                                </thead>
                                <tbody>
                                    {batches.map(batch => (
                                        <tr key={batch.batchId}>
                                            <td>{batch.campaignName || batch.batchId}</td>
                                            <td>{formatTime(batch.createdAt)}</td>
                                            <td>{batch.total}</td>
                                            <td>{batch.active}</td>
                                            <td>{batch.shown}</td>
                                            <td>{batch.blocked}</td>
                                            <td>{batch.acknowledged}</td>
                                            <td>
                                                {batch.active > 0 &&
                                                    <button
                                                        className='userNoticesLink userNoticesLink--danger'
                                                        disabled={changePending}
                                                        onClick={() => setStopping(batch)}
                                                    >
                                                        {t.gettext('Stop')}
                                                    </button>
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    }
                </div>
            }

            {tab === 'user' &&
                <div className='userNoticesCard'>
                    <NoticeUserPicker onSelect={pickUser}/>
                    {user &&
                        <div className='userNoticesUserRow'>
                            <span>{user.name || user.email} · {user.email}</span>
                            <button
                                className='userNoticesNew'
                                onClick={() => props.history.push({pathname: '/user_messages/new', state: {user}})}
                            >
                                {t.gettext('Send message to this user')}
                            </button>
                        </div>
                    }
                    {listPending && <Spinner/>}
                    {user && !listPending && !notices.length &&
                        <div className='userNoticesMuted'>{t.gettext('This user has no messages')}</div>
                    }
                    {notices.length > 0 &&
                        <div className='userNoticesTableScroll'>
                            <table className='userNoticesTable'>
                                <thead>
                                    <tr>
                                        <th>{t.gettext('Message')}</th>
                                        <th>{t.gettext('Status')}</th>
                                        <th>{t.gettext('Button')}</th>
                                        <th>{t.gettext('Created')}</th>
                                        <th/>
                                    </tr>
                                </thead>
                                <tbody>
                                    {notices.map(notice => (
                                        <tr key={notice.id}>
                                            <td>{notice.campaignName || `#${notice.id}`}</td>
                                            <td>{notice.active ? phaseLabel(t, notice.phase) : t.gettext('Inactive')}</td>
                                            <td>{actionLabel(t, notice)}</td>
                                            <td>{formatTime(notice.createdAt)}</td>
                                            <td className='userNoticesActions'>
                                                <button className='userNoticesLink' onClick={() => setDetails(notice)}>
                                                    {t.gettext('Details')}
                                                </button>
                                                {notice.active && notice.phase !== 'BLOCKING' &&
                                                    <button
                                                        className='userNoticesLink userNoticesLink--danger'
                                                        disabled={changePending}
                                                        onClick={() => dispatch(changeUserNotice(notice.id, 'block_now'))}
                                                    >
                                                        {t.gettext('Block now')}
                                                    </button>
                                                }
                                                {notice.active &&
                                                    <button
                                                        className='userNoticesLink'
                                                        disabled={changePending}
                                                        onClick={() => dispatch(changeUserNotice(notice.id, 'deactivate'))}
                                                    >
                                                        {t.gettext('Deactivate')}
                                                    </button>
                                                }
                                                {!notice.active &&
                                                    <button
                                                        className='userNoticesLink'
                                                        disabled={changePending}
                                                        onClick={() => dispatch(changeUserNotice(notice.id, 'activate'))}
                                                    >
                                                        {t.gettext('Activate')}
                                                    </button>
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    }
                </div>
            }

            {stopping &&
                <ConfirmationModal
                    text={`${t.gettext('Stop this campaign for all users who still have it?')} ${stopping.campaignName || ''}`}
                    closeMethod={() => setStopping(null)}
                    confirmMethod={stopBatch}
                    confirmButtonText={t.gettext('Stop')}
                />
            }
            {details && <UserNoticeDetailsModal notice={details} closeMethod={() => setDetails(null)}/>}
        </div>
    );
};

export default UserNotices;
